import React, { useEffect, useRef, useState } from 'react';

import { createPortal } from 'react-dom';
import OutsideClickHandler from 'react-outside-click-handler';
import { useDispatch, useSelector } from 'react-redux';

import MenuActionsHelper from 'components/shared/MenuActionsHelper';
import { useEscAction } from 'hooks/useEscAction';
import useForceUpdate from 'hooks/useForceUpdate';
import { isModalOpenedSelector } from 'store/modal/selectors';
import { getPage } from 'store/page/thunks';
import logger from 'utils/logger';
import noop from 'utils/noop';

import { getNormalizedPositionStyles, useEditorContext, EDITING_BLOCK_TYPES } from '../context';
import AreaSelectPage from './AreaSelect';
import styles from './index.module.css';
import PropertySelectPage from './PropertySelect';
import RoomPage from './Rooms';

const PAGES = {
  ROOMS: 'ROOMS',
  AREA_SELECT: 'AREA_SELECT',
  PROPERTY_SELECT: 'PICK_PROPERTY',
};

const PAGE_ORDER = [PAGES.PROPERTY_SELECT, PAGES.ROOMS, PAGES.AREA_SELECT];

function PageEntityEditor() {
  const dispatch = useDispatch();
  const { editorState, editingBlockState, resetEditingBlockState, mountEl } = useEditorContext();
  const meta = editorState.getCurrentContent().getEntity(editingBlockState.blockKey).getData();
  const [pageData, setPageData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const isRoomPreviewModalOpen = useSelector(isModalOpenedSelector('ROOM_PREVIEW'));
  const isAreaPreviewModalOpen = useSelector(isModalOpenedSelector('AREA_PREVIEW'));
  const [currentPage, setCurrentPage] = useState(PAGES.ROOMS);
  useEscAction(resetEditingBlockState);
  const containerRef = useRef();
  const forceUpdate = useForceUpdate();

  useEffect(() => {
    const run = async () => {
      try {
        setIsLoading(true);
        const page = await dispatch(getPage(meta.id, true));

        setPageData(page);
      } finally {
        setIsLoading(false);
        setTimeout(() => forceUpdate(), 0);
      }
    };

    run();
  }, []);

  let localOrder = [...PAGE_ORDER];

  const handleNextPage = () => {
    const currentPageIdx = localOrder.indexOf(currentPage);
    if (currentPageIdx === -1) {
      logger.error('[PageEntityEditor][handleNextPage] current page not found');
      return;
    }

    const nextPage = localOrder[currentPageIdx + 1];

    if (!nextPage) {
      logger.error('[PageEntityEditor][handleNextPage] next page not found');
      return;
    }
    containerRef.current = null;
    setCurrentPage(nextPage);

    setTimeout(() => forceUpdate(), 0); // force update to recalculate position with new ref
  };
  const handlePrevPage = () => {
    const currentPageIdx = localOrder.indexOf(currentPage);
    if (currentPageIdx === -1) {
      logger.error('[PageEntityEditor][handlePrevPage] current page not found');
      return;
    }

    const prevPage = localOrder[currentPageIdx - 1];
    if (!prevPage) {
      logger.error('[PageEntityEditor][handlePrevPage] prev page not found');
      return;
    }
    containerRef.current = null;
    setCurrentPage(prevPage);

    setTimeout(() => forceUpdate(), 0); // force update to recalculate position with new ref
  };

  if (isLoading) {
    return null;
  }

  return createPortal(
    <OutsideClickHandler
      onOutsideClick={isRoomPreviewModalOpen || isAreaPreviewModalOpen ? noop : resetEditingBlockState}
    >
      <div
        className={styles.container}
        ref={containerRef}
        style={getNormalizedPositionStyles(editingBlockState.blockRect, mountEl, containerRef.current)}
      >
        {currentPage === PAGES.PROPERTY_SELECT && (
          <PropertySelectPage
            ref={containerRef}
            property={pageData}
            onNextPage={handleNextPage}
            onPrevPage={handlePrevPage}
          />
        )}
        {currentPage === PAGES.ROOMS && (
          <RoomPage
            ref={containerRef}
            property={pageData}
            entityMeta={meta}
            onNextPage={handleNextPage}
            onPrevPage={handlePrevPage}
          />
        )}
        {currentPage === PAGES.AREA_SELECT && (
          <AreaSelectPage
            ref={containerRef}
            property={pageData}
            onNextPage={handleNextPage}
            onPrevPage={handlePrevPage}
          />
        )}
        <MenuActionsHelper escOnly />
      </div>
    </OutsideClickHandler>,
    mountEl,
  );
}

function PageEntityEditorContainer() {
  const { editingBlockState } = useEditorContext();

  if (!editingBlockState.isOpen || editingBlockState.blockType !== EDITING_BLOCK_TYPES.ENTITY_EDITOR) {
    return null;
  }

  return <PageEntityEditor />;
}

export default PageEntityEditorContainer;
